import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { useLink } from 'routes/cmsRoutes'
import { CREATE_QUESTION_BANK } from 'queries/questionBanks'
import { GET_LIST_QUESTION_BANK_CATEGORY, GET_ONE_QUESTION_BANK_CATEGORY } from 'queries/questionBankCategory'
import { CreateQuestionBankInput } from '__generatedTypes__/globalTypes'
import { createQuestionBank, createQuestionBankVariables } from '__generatedTypes__/createQuestionBank'
import {
  findOneQuestionBankCategory,
  findOneQuestionBankCategoryVariables,
} from '__generatedTypes__/findOneQuestionBankCategory'
import {
  findAllQuestionBankCategory,
  findAllQuestionBankCategoryVariables,
} from '__generatedTypes__/findAllQuestionBankCategory'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form } from '../Form'
import { getCBSDashboardLink, getQBDashboardLink, isCaseScenarioSubject, isSubjectWithoutQBCategory } from '../utils'

export const Create = () => {
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const categoryId = searchParams.get('categoryId')
  const subjectId = searchParams.get('subjectId')
  const defaultOrder = +(searchParams.get('defaultOrder') ?? 0)
  const tabIndex = +(searchParams.get('tabIndex') ?? 0)
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [create] = useMutation<createQuestionBank, createQuestionBankVariables>(CREATE_QUESTION_BANK)
  const categoryState = useQuery<findOneQuestionBankCategory, findOneQuestionBankCategoryVariables>(
    GET_ONE_QUESTION_BANK_CATEGORY,
    {
      variables: {
        params: String(categoryId),
      },
      skip: !categoryId,
    }
  )
  const category = categoryState.data?.findOneQuestionBankCategory
  const categoriesState = useQuery<findAllQuestionBankCategory, findAllQuestionBankCategoryVariables>(
    GET_LIST_QUESTION_BANK_CATEGORY,
    {
      variables: {
        params: {
          where: {
            subject: {
              id: String(subjectId),
            },
          },
        },
      },
      skip: !!categoryId || !subjectId,
    }
  )
  const categories = categoriesState.data?.findAllQuestionBankCategory.items ?? []

  const handleSubmit = useCallback(
    async (questionBank: CreateQuestionBankInput) => {
      try {
        const { data } = await create({
          variables: {
            params: {
              ...questionBank,
              history: populateHistoryWithUser(),
            },
          },
        })

        if (data?.createQuestionBank.id) {
          const subject = (category ?? categories[0])?.subject
          if (isCaseScenarioSubject(subject)) {
            navigate(getCBSDashboardLink(tabIndex))
          } else {
            navigate(getQBDashboardLink(tabIndex), undefined, {
              searchParams: new URLSearchParams({
                subjectId: String(subject?.id),
                categoryId: String(category?.id),
                tabIndex: String(tabIndex),
              }),
            })
          }

          toast.success('Question Bank created successfully')
        } else {
          toast.error('Failed to create Question Bank')
        }
      } catch (error) {
        toast.error('Failed to create Question Bank')
      }
    },
    [populateHistoryWithUser, create, navigate, category, categories]
  )

  useEffect(() => {
    if (
      // QB with Category
      (!categoryId || (!categoryState.loading && !category)) &&
      // QB without Category
      (!subjectId ||
        (!categoriesState.loading && (!categories.length || !isSubjectWithoutQBCategory(categories[0]?.subject))))
    ) {
      navigate(getQBDashboardLink(tabIndex))
      toast.error('Failed to load data, redirecting to Question Bank List')
    }
  }, [categoryId, subjectId, categoryState.loading, categoriesState, category, categories])

  if (!categoryId && !subjectId) {
    return null
  }

  if (((categoryState.loading || !category) && (categoriesState.loading || !categories.length)) || loadingHistory) {
    return <PageLoader />
  }

  return (
    <Form
      questionBankOrCategoryId={categoryId ?? categories[0].id}
      subject={category?.subject ?? categories[0].subject}
      subjectCategories={categories}
      showMembershipLevelSelect={!categoryId && isSubjectWithoutQBCategory(categories[0]?.subject)}
      onSubmit={handleSubmit}
      defaultOrder={defaultOrder}
      tabIndex={tabIndex}
    />
  )
}
